import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getCountFromServer, query, where, orderBy, doc, updateDoc, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import { db } from '../config/firebase';
import { isSuperAdmin } from '../utils/roleManager';
import { BIBLE_BOOKS, getVersesForChapter, getChaptersForBook } from '../utils/bibleData';
import { testCompletedPlansBackfill, cleanupTestData } from '../utils/testCompletedPlansBackfill';
import {
  ChartBarIcon, UsersIcon, HomeIcon, BookOpenIcon, PresentationChartLineIcon,
  BeakerIcon, CurrencyDollarIcon, WrenchScrewdriverIcon, MicrophoneIcon,
  ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon,
  CloudArrowDownIcon, ArrowLeftIcon, RectangleStackIcon, TrashIcon, BellAlertIcon
} from '@heroicons/react/24/outline';
import { formatPassageLabel } from '../utils/passagePlans';
import { parsePlansWorkbook, downloadTemplateWorkbook } from '../utils/bulkPlanImport';
import { CloudArrowUpIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

// Parses a single comma-separated segment like "Genesis 1" or "Exodus 1-10"
// against BIBLE_BOOKS. Returns null if it doesn't match a real book/chapter
// range, so the pill UI can flag it instead of silently accepting garbage.
function parseChapterSegment(segment) {
  const trimmed = segment.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(.+?)\s+(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  const [, bookPart, startStr, endStr] = match;
  const book = BIBLE_BOOKS.find(b => b.name.toLowerCase() === bookPart.trim().toLowerCase());
  if (!book) return null;
  const start = parseInt(startStr, 10);
  const end = endStr ? parseInt(endStr, 10) : start;
  if (start < 1 || end > book.chapters || start > end) return null;
  return { book, start, end };
}

// Enhanced Chapter Input Component with Quick Add UI. Defined at module
// scope (not inside SuperAdmin) — nesting a component definition inside
// another component's render body creates a new component identity every
// render, which remounts the input and drops focus after each keystroke.
function SmartChapterInput({ value, onChange, placeholder, id, label }) {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [selectedBook, setSelectedBook] = useState('');
  const [chapterRange, setChapterRange] = useState({ start: 1, end: 1 });
  const [localErrors, setLocalErrors] = useState([]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Simple validation
    const errors = [];
    if (newValue.trim() && !newValue.includes(' ')) {
      errors.push('Please include chapter numbers (e.g., "Genesis 1")');
    }
    setLocalErrors(errors);
  };

  // Pasting a spreadsheet column gives one "Book Chapter" per line with no
  // commas — convert those line breaks to comma separators so pills still
  // generate, instead of the whole paste becoming one unparseable segment.
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (!pasted.includes('\n')) return; // single line — default paste is fine
    e.preventDefault();

    const lines = pasted.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    let insertion = lines.join(', ');

    const textarea = e.target;
    const before = value.slice(0, textarea.selectionStart);
    const after = value.slice(textarea.selectionEnd);
    if (before.trim() && !/,\s*$/.test(before)) insertion = ', ' + insertion;
    // Always close with a trailing comma (unless "after" already starts with
    // one) — a paste is a completed action, so every pasted line should show
    // as a confirmed pill immediately instead of the last one being treated
    // as "still being typed".
    if (!/^\s*,/.test(after)) insertion = insertion + ', ';

    onChange(before + insertion + after);
  };

  // Pills for every segment the user has "closed off" with a comma — the
  // in-progress segment they're still typing (no trailing comma yet) stays
  // as plain text so a pill doesn't pop up mid-keystroke.
  const trailingComma = /,\s*$/.test(value);
  const allParts = value.split(',');
  const inProgress = trailingComma ? '' : (allParts[allParts.length - 1] || '').trim();
  const confirmedSegments = (trailingComma ? allParts : allParts.slice(0, -1))
    .map(s => s.trim())
    .filter(Boolean);

  const removePill = (indexToRemove) => {
    const remaining = confirmedSegments.filter((_, i) => i !== indexToRemove);
    const newValue = inProgress
      ? [...remaining, inProgress].join(', ')
      : remaining.join(', ') + (remaining.length ? ', ' : '');
    onChange(newValue);
  };

  const addChapterRange = () => {
    if (!selectedBook) return;
    
    const book = BIBLE_BOOKS.find(b => b.id === selectedBook);
    if (!book) return;
    
    const start = Math.min(chapterRange.start, chapterRange.end);
    const end = Math.max(chapterRange.start, chapterRange.end);
    
    // Validate range
    if (start < 1 || end > book.chapters) {
      alert(`Invalid range. ${book.name} has ${book.chapters} chapters.`);
      return;
    }
    
    let newChapters = '';
    if (start === end) {
      newChapters = `${book.name} ${start}`;
    } else {
      newChapters = `${book.name} ${start}-${end}`;
    }
    
    // Add to existing value
    const currentValue = value.trim();
    const newValue = currentValue ? `${currentValue}, ${newChapters}` : newChapters;
    onChange(newValue);
    
    // Reset form
    setSelectedBook('');
    setChapterRange({ start: 1, end: 1 });
    setShowQuickAdd(false);
  };

  const addSingleChapter = (bookId, chapter) => {
    const book = BIBLE_BOOKS.find(b => b.id === bookId);
    if (!book) return;
    
    const newChapter = `${book.name} ${chapter}`;
    const currentValue = value.trim();
    const newValue = currentValue ? `${currentValue}, ${newChapter}` : newChapter;
    onChange(newValue);
  };

  const addAllChapters = (bookId) => {
    const book = BIBLE_BOOKS.find(b => b.id === bookId);
    if (!book) return;
    
    const newChapters = `${book.name} 1-${book.chapters}`;
    const currentValue = value.trim();
    const newValue = currentValue ? `${currentValue}, ${newChapters}` : newChapters;
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <textarea
          id={id}
          value={value}
          onChange={handleInputChange}
          onPaste={handlePaste}
          rows={4}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
            localErrors.length > 0 ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder={placeholder}
        />

        {/* Confirmed chapter pills — one per comma-separated segment */}
        {confirmedSegments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {confirmedSegments.map((segment, index) => {
              const parsed = parseChapterSegment(segment);
              return (
                <span
                  key={`${segment}-${index}`}
                  className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm font-medium ${
                    parsed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}
                  title={parsed ? undefined : 'Not recognized as a valid book/chapter — check spelling'}
                >
                  {parsed ? (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.598c.75 1.334-.213 2.98-1.742 2.98H3.48c-1.53 0-2.493-1.646-1.743-2.98L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  {segment}
                  <button
                    type="button"
                    onClick={() => removePill(index)}
                    className={`ml-0.5 rounded-full p-0.5 hover:bg-black/10 ${parsed ? 'text-green-700' : 'text-amber-700'}`}
                    title={`Remove "${segment}"`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Validation Errors */}
        {localErrors.length > 0 && (
          <div className="mt-2">
            {localErrors.map((error, index) => (
              <div key={index} className="text-sm text-red-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Add Section */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Quick Add Chapters</h4>
          <button
            type="button"
            onClick={() => setShowQuickAdd(!showQuickAdd)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showQuickAdd ? 'Hide' : 'Show'} Quick Add
          </button>
        </div>
        
        {showQuickAdd && (
          <div className="space-y-4">
            {/* Book Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Book
              </label>
              <select
                value={selectedBook}
                onChange={(e) => {
                  setSelectedBook(e.target.value);
                  if (e.target.value) {
                    const book = BIBLE_BOOKS.find(b => b.id === e.target.value);
                    setChapterRange({ start: 1, end: book?.chapters || 1 });
                  }
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a book...</option>
                {BIBLE_BOOKS.map(book => (
                  <option key={book.id} value={book.id}>
                    {book.name} ({book.chapters} chapters)
                  </option>
                ))}
              </select>
            </div>

            {selectedBook && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Chapter Range Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chapter Range
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="1"
                      max={BIBLE_BOOKS.find(b => b.id === selectedBook)?.chapters || 1}
                      value={chapterRange.start}
                      onChange={(e) => setChapterRange(prev => ({ ...prev, start: parseInt(e.target.value) || 1 }))}
                      className="block w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Start"
                    />
                    <span className="flex items-center text-gray-500">to</span>
                    <input
                      type="number"
                      min="1"
                      max={BIBLE_BOOKS.find(b => b.id === selectedBook)?.chapters || 1}
                      value={chapterRange.end}
                      onChange={(e) => setChapterRange(prev => ({ ...prev, end: parseInt(e.target.value) || 1 }))}
                      className="block w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="End"
                    />
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-col space-y-2">
                  <button
                    type="button"
                    onClick={addChapterRange}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    Add Range
                  </button>
                  <button
                    type="button"
                    onClick={() => addAllChapters(selectedBook)}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                  >
                    Add All Chapters
                  </button>
                </div>

                {/* Chapter Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Or click individual chapters:
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-white">
                    <div className="grid grid-cols-5 gap-1">
                      {Array.from({ length: BIBLE_BOOKS.find(b => b.id === selectedBook)?.chapters || 0 }, (_, i) => i + 1).map(chapter => (
                        <button
                          key={chapter}
                          type="button"
                          onClick={() => addSingleChapter(selectedBook, chapter)}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-blue-100 rounded border"
                        >
                          {chapter}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Help Text */}
      <p className="text-sm text-gray-500">
        <strong>Formats supported:</strong> "Genesis 1", "Genesis 1-10", "Genesis 1, Exodus 20, Psalm 23"
      </p>
    </div>
  );
}

function SuperAdmin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFamilies: 0,
    totalRecordings: 0,
    activeUsers: 0,
    premiumSubscribers: 0,
    totalRevenue: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentFamilies, setRecentFamilies] = useState([]);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState({ current: 0, total: 0, status: '' });
  const [isMigratingData, setIsMigratingData] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ current: 0, total: 0, status: '' });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    databaseStatus: 'healthy',
    storageStatus: 'healthy',
    authStatus: 'healthy',
    lastBackup: new Date().toISOString()
  });
  
  // Plan creation state
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [planChapters, setPlanChapters] = useState('');
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [existingPlans, setExistingPlans] = useState([]);
  
  // Plan editing state
  const [editingPlan, setEditingPlan] = useState(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editPlanName, setEditPlanName] = useState('');
  const [editPlanDescription, setEditPlanDescription] = useState('');
  const [editPlanChapters, setEditPlanChapters] = useState('');
  
  // Bulk plan import (spreadsheet drag-and-drop) state
  const [importFileName, setImportFileName] = useState('');
  const [importDragActive, setImportDragActive] = useState(false);
  const [importDrafts, setImportDrafts] = useState([]);
  const [importParseError, setImportParseError] = useState('');
  const [importBusyKey, setImportBusyKey] = useState(null); // key of the draft currently being created/updated

  // Smart input state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Plan analytics state
  const [planAnalytics, setPlanAnalytics] = useState([]);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  // Shared audio playback state (used by Restore tab)
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Apple review demo family seeding
  const [appleSeedLoading, setAppleSeedLoading] = useState(false);
  const [appleSeedResult, setAppleSeedResult] = useState(null);
  const [appleSeedError, setAppleSeedError] = useState('');

  // Passages Plans — global oversight (all public + private plans, any family)
  const [passagePlans, setPassagePlans] = useState([]);
  const [passagePlansLoading, setPassagePlansLoading] = useState(false);
  const [passagePlansLoaded, setPassagePlansLoaded] = useState(false);
  const [deletingPassagePlanId, setDeletingPassagePlanId] = useState(null);

  // Restore from backup state
  const [restoreCategory, setRestoreCategory] = useState(null);
  const [restoreRecordings, setRestoreRecordings] = useState([]);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoringIds, setRestoringIds] = useState(new Set());
  const [restoredIds, setRestoredIds] = useState(new Set());
  const [batchRestoring, setBatchRestoring] = useState(false);
  const [batchRestoreProgress, setBatchRestoreProgress] = useState({ done: 0, total: 0 });

  // Check if user is super admin
  const userIsSuperAdmin = isSuperAdmin(currentUser);

  useEffect(() => {
    if (!userIsSuperAdmin) {
      navigate('/');
      return;
    }

    fetchSuperAdminData();
    fetchExistingPlans();
    fetchPlanAnalytics();
  }, [userIsSuperAdmin, navigate]);

  // Lazy-load Passages Plans the first time that tab is opened
  useEffect(() => {
    if (activeTab === 'passages' && !passagePlansLoaded && !passagePlansLoading) {
      loadPassagePlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Filter users based on search term
  useEffect(() => {
    if (userSearchTerm.trim() === '') {
      setFilteredUsers(recentUsers);
    } else {
      const filtered = recentUsers.filter(user => {
        const searchLower = userSearchTerm.toLowerCase();
        return (
          user.email?.toLowerCase().includes(searchLower) ||
          user.displayName?.toLowerCase().includes(searchLower) ||
          user.familyRelation?.toLowerCase().includes(searchLower) ||
          user.bio?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredUsers(filtered);
    }
  }, [userSearchTerm, recentUsers]);

  // Filter families based on search term
  useEffect(() => {
    if (familySearchTerm.trim() === '') {
      setFilteredFamilies(recentFamilies);
    } else {
      const filtered = recentFamilies.filter(family => {
        const searchLower = familySearchTerm.toLowerCase();
        return (
          family.name?.toLowerCase().includes(searchLower) ||
          family.description?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredFamilies(filtered);
    }
  }, [familySearchTerm, recentFamilies]);

  const [notificationTestMode, setNotificationTestMode] = useState(
    currentUser?.notificationTestMode === true
  );
  const [testModeLoading, setTestModeLoading] = useState(false);

  const handleNotificationTestModeToggle = async (newValue) => {
    setTestModeLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { notificationTestMode: newValue });
      setNotificationTestMode(newValue);
    } catch (e) {
      console.error('Failed to update notification test mode:', e);
    } finally {
      setTestModeLoading(false);
    }
  };

  // Temporary backfill tool for the birthday age-gate: lets admins clear
  // pre-existing users (all known to be adults) so they aren't blocked from
  // creating a family by the new 18+ check, without each of them re-entering
  // their birthday via Account settings.
  const handleBirthdayChange = async (userId, newBirthday) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        birthday: newBirthday || null,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
      });
      fetchSuperAdminData();
    } catch (error) {
      console.error('Error updating birthday:', error);
      alert('Failed to update birthday');
    }
  };

  const handleSuperAdminToggle = async (userId, newStatus) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        superAdmin: newStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      fetchSuperAdminData();
    } catch (error) {
      console.error('Error updating super admin status:', error);
      alert('Failed to update super admin status');
    }
  };

  const handleBetaToggle = async (userId, newStatus) => {
    try {
      // isBeta is what actually bypasses plan limits (see planLimits.js) —
      // that behavior is unchanged. Alongside it, grant/revoke a real $0
      // Family Legacy subscription in Stripe (via the Friends Access
      // coupon) so the account shows a legitimate, trackable plan instead
      // of no subscription at all. Non-fatal if this Stripe step fails —
      // the isBeta bypass still works either way.
      await updateDoc(doc(db, 'users', userId), {
        isBeta: newStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fns = getFunctions(getApp());
        const fn = httpsCallable(fns, newStatus ? 'grantFriendsAccess' : 'revokeFriendsAccess');
        await fn({ userId });
      } catch (stripeError) {
        console.error('Error syncing Friends Access subscription in Stripe:', stripeError);
        alert('Friends Access was granted, but the Stripe subscription sync failed — the account still has full access. Check the Cloud Functions logs.');
      }
      fetchSuperAdminData();
    } catch (error) {
      console.error('Error updating beta status:', error);
      alert('Failed to update beta status');
    }
  };

  const handleResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert(`Failed to send password reset email: ${error.message}`);
    }
  };

  const handleSeedAppleReviewFamily = async () => {
    console.log('🍎 [Apple Review Seed] Starting seedAppleReviewFamily call…');
    setAppleSeedLoading(true);
    setAppleSeedError('');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fns = getFunctions(getApp());
      const fn = httpsCallable(fns, 'seedAppleReviewFamily');
      const { data } = await fn();
      console.log('🍎 [Apple Review Seed] Success:', data);
      setAppleSeedResult(data);
    } catch (error) {
      console.error('🍎 [Apple Review Seed] Failed:', {
        code: error.code,
        message: error.message,
        details: error.details,
        raw: error,
      });
      setAppleSeedError(error.message || 'Failed to seed demo family');
    } finally {
      setAppleSeedLoading(false);
    }
  };

  const loadPassagePlans = async () => {
    setPassagePlansLoading(true);
    try {
      // Rules grant super admins read access to every passagePlans doc
      // regardless of familyId/visibility, so this plain query returns
      // everything — public and private, across every family.
      const snap = await getDocs(collection(db, 'passagePlans'));
      const plansData = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Resolve owning family names for display
      const familyIds = [...new Set(plansData.map(p => p.familyId).filter(Boolean))];
      const familyDocs = await Promise.all(
        familyIds.map(fid => getDocs(query(collection(db, 'families'), where('__name__', '==', fid))))
      );
      const familyNameById = {};
      familyDocs.forEach(fSnap => {
        fSnap.docs.forEach(fDoc => { familyNameById[fDoc.id] = fDoc.data().name || 'Unnamed Family'; });
      });

      plansData.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setPassagePlans(plansData.map(p => ({ ...p, familyName: familyNameById[p.familyId] || 'Unknown Family' })));
      setPassagePlansLoaded(true);
    } catch (error) {
      console.error('Error loading passage plans:', error);
    } finally {
      setPassagePlansLoading(false);
    }
  };

  const handleDeletePassagePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"? This cannot be undone.`)) return;
    setDeletingPassagePlanId(plan.id);
    try {
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'passagePlans', plan.id));
      setPassagePlans(prev => prev.filter(p => p.id !== plan.id));
    } catch (error) {
      console.error('Error deleting passage plan:', error);
      alert(`Failed to delete plan: ${error.message}`);
    } finally {
      setDeletingPassagePlanId(null);
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
  };

  const handleBackToFamilies = () => {
    setSelectedFamily(null);
  };

  // Smart input validation functions
  const findBookSuggestions = (input) => {
    if (!input || input.length < 2) return [];
    
    const normalizedInput = input.toLowerCase().trim();
    const suggestions = [];
    
    BIBLE_BOOKS.forEach(book => {
      const bookName = book.name.toLowerCase();
      const bookId = book.id.toLowerCase();
      
      // Exact match
      if (bookName === normalizedInput || bookId === normalizedInput) {
        suggestions.unshift({ book, score: 100, type: 'exact' });
        return;
      }
      
      // Starts with input
      if (bookName.startsWith(normalizedInput) || bookId.startsWith(normalizedInput)) {
        suggestions.push({ book, score: 90, type: 'starts' });
        return;
      }
      
      // Contains input
      if (bookName.includes(normalizedInput) || bookId.includes(normalizedInput)) {
        suggestions.push({ book, score: 70, type: 'contains' });
        return;
      }
      
      // Fuzzy match (simple Levenshtein distance)
      const distance = levenshteinDistance(normalizedInput, bookName);
      if (distance <= 3 && normalizedInput.length >= 3) {
        suggestions.push({ book, score: 50 - distance * 10, type: 'fuzzy' });
      }
    });
    
    return suggestions.sort((a, b) => b.score - a.score).slice(0, 5);
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const validateChapterInput = (input) => {
    const errors = [];
    const entries = input.split(',').map(entry => entry.trim()).filter(entry => entry);
    
    entries.forEach((entry, index) => {
      const parts = entry.trim().split(/\s+/);
      if (parts.length < 2) {
        errors.push(`Entry ${index + 1}: "${entry}" - Missing chapter number`);
        return;
      }
      
      const bookPart = parts.slice(0, -1).join(' ').toLowerCase();
      const chapterPart = parts[parts.length - 1];
      
      // Check if chapter is a number
      if (!/^\d+$/.test(chapterPart)) {
        errors.push(`Entry ${index + 1}: "${entry}" - Chapter must be a number`);
        return;
      }
      
      // Check if book exists
      const bookExists = BIBLE_BOOKS.some(book => 
        book.name.toLowerCase() === bookPart || 
        book.id.toLowerCase() === bookPart
      );
      
      if (!bookExists) {
        const suggestions = findBookSuggestions(bookPart);
        if (suggestions.length > 0) {
          errors.push(`Entry ${index + 1}: "${entry}" - Did you mean "${suggestions[0].book.name}"?`);
        } else {
          errors.push(`Entry ${index + 1}: "${entry}" - Book not found`);
        }
      }
    });
    
    return errors;
  };

  const handleSmartInputChange = (value, setter) => {
    setter(value);
    setCurrentInput(value);
    
    // Get cursor position (approximate)
    const cursorPos = value.length;
    setCursorPosition(cursorPos);
    
    // Find the current word being typed
    const words = value.split(/\s+/);
    const currentWord = words[words.length - 1] || '';
    
    if (currentWord.length >= 2) {
      const newSuggestions = findBookSuggestions(currentWord);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
    
    // Validate the entire input
    const errors = validateChapterInput(value);
    setValidationErrors(errors);
  };

  const insertSuggestion = (suggestion, setter) => {
    const words = currentInput.split(/\s+/);
    words[words.length - 1] = suggestion.book.name;
    const newValue = words.join(' ');
    setter(newValue);
    setCurrentInput(newValue);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Plan creation functions
  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setEditPlanName(plan.name);
    setEditPlanDescription(plan.description);
    setEditPlanChapters(plan.chapters?.map(ch => `${ch.book} ${ch.chapter}`).join(', ') || '');
    setIsEditingPlan(true);
  };

  const handleSaveEdit = async () => {
    if (!editPlanName.trim() || !editPlanDescription.trim() || !editPlanChapters.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const parsedChapters = parseChapters(editPlanChapters);
      
      // Enhance chapters with verse counts and URLs
      const enhancedChapters = parsedChapters.map(chapter => {
        const totalVerses = getTotalVersesForChapter(chapter.book, chapter.chapter);
        return {
          ...chapter,
          totalVerses,
          verseUrls: generateVerseUrls(chapter.book, chapter.chapter, totalVerses)
        };
      });

      const planData = {
        name: editPlanName.trim(),
        description: editPlanDescription.trim(),
        chapters: enhancedChapters,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'plans', editingPlan.id), planData);
      
      // Reset edit form
      setEditingPlan(null);
      setEditPlanName('');
      setEditPlanDescription('');
      setEditPlanChapters('');
      setIsEditingPlan(false);
      
      // Refresh plans list
      await fetchExistingPlans();
      
      alert('Plan updated successfully!');
    } catch (error) {
      console.error('Error updating plan:', error);
      alert('Failed to update plan: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditPlanName('');
    setEditPlanDescription('');
    setEditPlanChapters('');
    setIsEditingPlan(false);
  };

  const handleDeletePlan = async () => {
    if (!editingPlan) return;
    if (!window.confirm(`Delete "${editingPlan.name}"? This cannot be undone.`)) return;
    try {
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'plans', editingPlan.id));
      await fetchExistingPlans();
      handleCancelEdit();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan: ' + error.message);
    }
  };

  const fetchExistingPlans = async () => {
    try {
      const plansSnapshot = await getDocs(collection(db, 'plans'));
      const plansData = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExistingPlans(plansData);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const parseChapters = (chaptersText) => {
    const chapters = chaptersText.split(',').map(chapter => chapter.trim()).filter(Boolean);
    const parsedChapters = [];

    for (const chapterText of chapters) {
      // Parse formats like "Matthew 5-7", "1 Corinthians 13", "John 1", "Romans 5".
      // Book name is matched non-greedily so multi-word books ("1 Kings",
      // "Song of Solomon") work too — a single-word-only regex here used to
      // silently drop every multi-word-book chapter with no warning.
      const match = chapterText.match(/^(.+?)\s+(\d+)(?:-(\d+))?$/i);
      if (!match) continue;
      const [, bookPart, startChapter, endChapter] = match;
      // Look up the canonical book id (e.g. "1-corinthians") instead of just
      // lowercasing the typed text, which would store "1 corinthians" —
      // inconsistent with every other book id in the app.
      const book = BIBLE_BOOKS.find(b => b.name.toLowerCase() === bookPart.trim().toLowerCase());
      if (!book) continue;
      const start = parseInt(startChapter);
      const end = Math.min(endChapter ? parseInt(endChapter) : start, book.chapters);

      // Create individual chapter entries for ranges
      for (let i = start; i <= end; i++) {
        parsedChapters.push({
          book: book.id,
          chapter: i,
          startVerse: 1,
          endVerse: null,
          totalVerses: 0, // Will be calculated later
          recordedVerses: 0,
          isComplete: false,
          chapterUrl: `/listen?book=${book.id}&chapter=${i}`,
          verseUrls: [] // Will be generated later
        });
      }
    }

    return parsedChapters;
  };

  const getTotalVersesForChapter = (book, chapter) => {
    try {
      // Use the actual Bible data to get the correct verse count
      const verses = getVersesForChapter(book, chapter);
      return verses.length;
    } catch (error) {
      console.error(`Error getting verse count for ${book} ${chapter}:`, error);
      // Fallback to a reasonable default if there's an error
      return 30;
    }
  };

  // Backfill family completion subcollection for existing families
  const backfillFamilyCompletion = async () => {
    if (!window.confirm('This will backfill the family completion subcollection for all existing families. Continue?')) {
      return;
    }

    console.log('🚀 BACKFILL SCRIPT: Starting family completion backfill process...');
    console.log('📊 BACKFILL SCRIPT: Data and access requirements:');
    console.log('   - READ ACCESS: families collection (to get all existing families)');
    console.log('   - READ ACCESS: users collection (to get family members)');
    console.log('   - READ ACCESS: recordings collection (to analyze existing recordings)');
    console.log('   - WRITE ACCESS: families/{familyId}/completedChapters/all-chapters (to create completion data)');
    console.log('   - DATA STRUCTURE: Will create documents with all 1,189 Bible chapters');
    console.log('   - BIBLE DATA: Using actual verse counts from bibleData.json (no hardcoded values)');

    setIsBackfilling(true);
    setBackfillProgress({ current: 0, total: 0, status: 'Starting backfill...' });

    try {
      // Import Firebase functions
      const { collection, getDocs, doc, setDoc, serverTimestamp, query, where, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const { BIBLE_BOOKS, getChaptersForBook, getVersesForChapter } = await import('../utils/bibleData');
      
      console.log('✅ BACKFILL SCRIPT: Firebase imports successful');
      console.log('🔍 BACKFILL SCRIPT: Querying families collection...');
      
      // Get all existing families
      const familiesSnapshot = await getDocs(collection(db, 'families'));
      const families = familiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      console.log(`📊 BACKFILL SCRIPT: Found ${families.length} families to process:`);
      families.forEach((family, index) => {
        console.log(`   ${index + 1}. Family ID: ${family.id}`);
        console.log(`      - Name: ${family.name || 'Unnamed'}`);
        console.log(`      - Created: ${family.createdAt ? new Date(family.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}`);
        console.log(`      - Members: ${family.memberCount || 0}`);
      });
      
      setBackfillProgress({ current: 0, total: families.length, status: `Found ${families.length} families to process` });
      
      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;
      
      for (const family of families) {
        try {
          console.log(`\n🔄 BACKFILL SCRIPT: Processing family ${processedCount + 1}/${families.length}`);
          console.log(`   Family ID: ${family.id}`);
          console.log(`   Family Name: ${family.name || 'Unnamed'}`);
          
          setBackfillProgress({ 
            current: processedCount, 
            total: families.length, 
            status: `Processing family: ${family.name || family.id}` 
          });
          
          // Get all users in this family
          console.log(`   👥 BACKFILL SCRIPT: Finding family members for ${family.id}...`);
          const usersSnapshot = await getDocs(
            query(
              collection(db, 'users'),
              where('familyIds', 'array-contains', family.id)
            )
          );
          
          const familyUserIds = usersSnapshot.docs.map(doc => doc.id);
          console.log(`   👥 BACKFILL SCRIPT: Found ${familyUserIds.length} family members:`, familyUserIds);
          
          if (familyUserIds.length === 0) {
            console.log(`   ⚠️  BACKFILL SCRIPT: No family members found for ${family.id}, skipping...`);
            processedCount++;
            continue;
          }
          
          // Get ALL recordings from ALL family members
          console.log(`   🎵 BACKFILL SCRIPT: Analyzing recordings from ${familyUserIds.length} family members...`);
          const recordingsSnapshot = await getDocs(
            query(
              collection(db, 'recordings'),
              where('userId', 'in', familyUserIds)
            )
          );
          
          const allRecordings = recordingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`   🎵 BACKFILL SCRIPT: Found ${allRecordings.length} total recordings from family members`);
          
          // Group recordings by book and chapter
          const recordingsByChapter = {};
          for (const recording of allRecordings) {
            const key = `${recording.book}_${recording.chapter}`;
            if (!recordingsByChapter[key]) {
              recordingsByChapter[key] = [];
            }
            recordingsByChapter[key].push(recording);
          }
          
          console.log(`   📊 BACKFILL SCRIPT: Recordings grouped into ${Object.keys(recordingsByChapter).length} unique chapters`);
          
          // Create completion data with all Bible chapters
          console.log(`   🏗️  BACKFILL SCRIPT: Creating completion data structure...`);
          const allChapters = [];
          
          for (const book of BIBLE_BOOKS) {
            const chapters = getChaptersForBook(book.id);
            for (const chapter of chapters) {
              const verses = getVersesForChapter(book.id, chapter);
              const chapterKey = `${book.id}_${chapter}`;
              const chapterRecordings = recordingsByChapter[chapterKey] || [];
              
              // Aggregate all recorded verses from all family members for this chapter
              const recordedVerses = new Set();
              for (const recording of chapterRecordings) {
                if (recording.verse) {
                  recordedVerses.add(recording.verse);
                }
              }
              
              const recordedVerseNumbers = Array.from(recordedVerses).sort((a, b) => a - b);
              const completedVerses = recordedVerseNumbers.length;
              const totalVerses = verses.length;
              const isCompleted = completedVerses >= totalVerses;
              
              allChapters.push({
                book: book.id,
                chapter: chapter,
                totalVerses: totalVerses,
                completedVerses: completedVerses,
                completed: isCompleted,
                recordedVerseNumbers: recordedVerseNumbers,
                lastUpdated: new Date(),
                createdAt: new Date()
              });
              
              // Log completion status for Proverbs 1 as requested
              if (book.id === 'proverbs' && chapter === 1) {
                console.log(`   📖 PROVERBS 1 ANALYSIS:`);
                console.log(`      - Total verses: ${totalVerses}`);
                console.log(`      - Recorded verses: ${completedVerses}`);
                console.log(`      - Recorded verse numbers: [${recordedVerseNumbers.join(', ')}]`);
                console.log(`      - Is completed: ${isCompleted}`);
                console.log(`      - Recordings found: ${chapterRecordings.length}`);
                chapterRecordings.forEach((rec, idx) => {
                  console.log(`         ${idx + 1}. User: ${rec.userId}, Verse: ${rec.verse}, File: ${rec.audioUrl}`);
                });
              }
            }
          }
          
          // Calculate total completed chapters
          const completedChapters = allChapters.filter(ch => ch.completed).length;
          
          console.log(`   📊 BACKFILL SCRIPT: Completion analysis for ${family.name || family.id}:`);
          console.log(`      - Total chapters: ${allChapters.length}`);
          console.log(`      - Completed chapters: ${completedChapters}`);
          console.log(`      - In-progress chapters: ${allChapters.filter(ch => !ch.completed && ch.completedVerses > 0).length}`);
          console.log(`      - Unstarted chapters: ${allChapters.filter(ch => ch.completedVerses === 0).length}`);
          
          // Create or update the completion document
          const completionRef = doc(db, `families/${family.id}/completedChapters`, 'all-chapters');
          await setDoc(completionRef, {
            chapters: allChapters,
            totalChapters: allChapters.length,
            completedChapters: completedChapters,
            lastUpdated: serverTimestamp(),
            createdAt: serverTimestamp()
          });
          
          console.log(`   ✅ BACKFILL SCRIPT: Successfully created completion data for family ${family.id}`);
          console.log(`   📊 BACKFILL SCRIPT: ${completedChapters} chapters marked as completed`);
          
          successCount++;
          processedCount++;
          
          // Small delay to prevent overwhelming Firestore
          await new Promise(resolve => setTimeout(resolve, 200));
          
        } catch (familyError) {
          console.error(`❌ BACKFILL SCRIPT: Error processing family ${family.id}:`, familyError);
          console.error(`❌ BACKFILL SCRIPT: Error details:`, {
            familyId: family.id,
            familyName: family.name || 'Unnamed',
            errorMessage: familyError.message,
            errorCode: familyError.code,
            errorStack: familyError.stack
          });
          errorCount++;
          processedCount++;
          // Continue with next family
        }
      }
      
      console.log(`\n🎉 BACKFILL SCRIPT: Backfill process completed!`);
      console.log(`📊 BACKFILL SCRIPT: Final statistics:`);
      console.log(`   - Total families processed: ${processedCount}`);
      console.log(`   - Successful creations: ${successCount}`);
      console.log(`   - Errors encountered: ${errorCount}`);
      console.log(`   - Success rate: ${((successCount / processedCount) * 100).toFixed(1)}%`);
      
      setBackfillProgress({ 
        current: processedCount, 
        total: families.length, 
        status: `Backfill complete! Processed ${processedCount} families (${successCount} successful, ${errorCount} errors)` 
      });
      
      // Refresh the families list
      console.log('🔄 BACKFILL SCRIPT: Refreshing SuperAdmin data...');
      await fetchSuperAdminData();
      console.log('✅ BACKFILL SCRIPT: SuperAdmin data refreshed');
      
    } catch (error) {
      console.error('❌ BACKFILL SCRIPT: Critical error during backfill process:', error);
      console.error('❌ BACKFILL SCRIPT: Error details:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack
      });
      setBackfillProgress({ 
        current: 0, 
        total: 0, 
        status: `Critical Error: ${error.message}` 
      });
    } finally {
      setIsBackfilling(false);
      console.log('🏁 BACKFILL SCRIPT: Backfill process finished');
    }
  };

  // Create family completion subcollection with all Bible chapters
  const createFamilyCompletionSubcollection = async (familyId, db) => {
    try {
      console.log(`🏗️  CREATE COMPLETION: Starting creation for family ${familyId}`);
      
      const { collection, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      
      console.log(`📚 CREATE COMPLETION: Processing ${BIBLE_BOOKS.length} Bible books...`);
      
      // Get all Bible books and chapters
      const allChapters = [];
      
      for (const book of BIBLE_BOOKS) {
        const chapters = getChaptersForBook(book.id);
        console.log(`   📖 CREATE COMPLETION: Processing ${book.name} (${book.id}) - ${chapters.length} chapters`);
        
        for (const chapter of chapters) {
          const verses = getVersesForChapter(book.id, chapter);
          const chapterData = {
            book: book.id,
            chapter: chapter,
            totalVerses: verses.length,
            completedVerses: 0,
            completed: false
            // Note: Removed serverTimestamp() from individual chapter objects
            // Firebase doesn't allow serverTimestamp() inside arrays
          };
          
          allChapters.push(chapterData);
          
          // Log first few chapters for verification
          if (allChapters.length <= 5) {
            console.log(`      📄 CREATE COMPLETION: Chapter ${chapter} - ${verses.length} verses`);
          }
        }
      }
      
      console.log(`📊 CREATE COMPLETION: Generated ${allChapters.length} chapter records`);
      console.log(`📋 CREATE COMPLETION: Sample chapter data:`, allChapters.slice(0, 3));
      
      // Create the completion subcollection
      const completionRef = doc(db, `families/${familyId}/completedChapters`, 'all-chapters');
      const completionData = {
        chapters: allChapters,
        totalChapters: allChapters.length,
        completedChapters: 0,
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      console.log(`💾 CREATE COMPLETION: Writing to Firestore document: families/${familyId}/completedChapters/all-chapters`);
      console.log(`📋 CREATE COMPLETION: Document data structure:`, {
        totalChapters: completionData.totalChapters,
        completedChapters: completionData.completedChapters,
        chaptersArrayLength: completionData.chapters.length,
        hasServerTimestamps: 'Yes (top-level only: lastUpdated, createdAt)',
        chapterTimestamps: 'No (removed to avoid Firebase array validation error)',
        sampleChapter: completionData.chapters[0]
      });
      
      await setDoc(completionRef, completionData);
      
      console.log(`✅ CREATE COMPLETION: Successfully created completion subcollection for family ${familyId}`);
      console.log(`📊 CREATE COMPLETION: Document contains ${allChapters.length} chapters with actual Bible verse counts`);
      
    } catch (error) {
      console.error(`❌ CREATE COMPLETION: Error creating completion subcollection for family ${familyId}:`, error);
      console.error(`❌ CREATE COMPLETION: Error details:`, {
        familyId: familyId,
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack
      });
      throw error;
    }
  };

  // Migration script to update family completion data based on existing recordings
  const migrateFamilyCompletionData = async () => {
    if (!window.confirm('This will analyze existing recordings and update family completion data. This may take a few minutes. Continue?')) {
      return;
    }

    console.log('🔄 MIGRATION SCRIPT: Starting family completion data migration...');
    console.log('📊 MIGRATION SCRIPT: This will:');
    console.log('   1. Read all existing recordings from all users');
    console.log('   2. Group recordings by family');
    console.log('   3. Determine which chapters are completed by each family');
    console.log('   4. Update family completion subcollections with correct data');

    setIsMigratingData(true);
    setMigrationProgress({ current: 0, total: 0, status: 'Starting migration...' });

    try {
      // Import Firebase functions
      const { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp, query, where } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      console.log('✅ MIGRATION SCRIPT: Firebase imports successful');
      
      // Step 1: Get all families
      console.log('🔍 MIGRATION SCRIPT: Fetching all families...');
      const familiesSnapshot = await getDocs(collection(db, 'families'));
      const families = familiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      console.log(`📊 MIGRATION SCRIPT: Found ${families.length} families to process`);
      setMigrationProgress({ current: 0, total: families.length, status: `Found ${families.length} families` });
      
      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;
      
      for (const family of families) {
        try {
          console.log(`\n🔄 MIGRATION SCRIPT: Processing family ${processedCount + 1}/${families.length}`);
          console.log(`   Family ID: ${family.id}`);
          console.log(`   Family Name: ${family.name || 'Unnamed'}`);
          
          setMigrationProgress({ 
            current: processedCount, 
            total: families.length, 
            status: `Processing family: ${family.name || family.id}` 
          });
          
          // Step 2: Get all users in this family
          console.log(`   👥 MIGRATION SCRIPT: Finding users in family ${family.id}...`);
          const usersSnapshot = await getDocs(
            query(
              collection(db, 'users'),
              where('familyIds', 'array-contains', family.id)
            )
          );
          
          const familyUsers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`   👥 MIGRATION SCRIPT: Found ${familyUsers.length} users in family`);
          
          if (familyUsers.length === 0) {
            console.log(`   ⚠️  MIGRATION SCRIPT: No users found in family ${family.id}, skipping`);
            processedCount++;
            continue;
          }
          
          // Step 3: Get all recordings from family users
          console.log(`   🎵 MIGRATION SCRIPT: Fetching recordings from family users...`);
          const userIds = familyUsers.map(user => user.id);
          const recordingsSnapshot = await getDocs(
            query(
              collection(db, 'recordings'),
              where('userId', 'in', userIds)
            )
          );
          
          const familyRecordings = recordingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`   🎵 MIGRATION SCRIPT: Found ${familyRecordings.length} recordings from family users`);
          
          // Step 4: Analyze recordings to determine completed chapters
          console.log(`   📊 MIGRATION SCRIPT: Analyzing recordings to determine completed chapters...`);
          const completedChapters = new Set();
          
          // Group recordings by chapter to properly analyze completion
          const recordingsByChapter = {};
          
          for (const recording of familyRecordings) {
            if (recording.book && recording.chapter) {
              const chapterKey = `${recording.book}_${recording.chapter}`;
              
              if (!recordingsByChapter[chapterKey]) {
                recordingsByChapter[chapterKey] = {
                  book: recording.book,
                  chapter: recording.chapter,
                  recordedVerses: new Set()
                };
              }
              
              // Add recorded verses to the set
              if (recording.verse) {
                // New format: single verse
                recordingsByChapter[chapterKey].recordedVerses.add(recording.verse);
              } else if (recording.startVerse && recording.endVerse) {
                // Old format: verse range
                for (let v = recording.startVerse; v <= recording.endVerse; v++) {
                  recordingsByChapter[chapterKey].recordedVerses.add(v);
                }
              } else if (recording.startVerse) {
                // Old format: single verse (startVerse only)
                recordingsByChapter[chapterKey].recordedVerses.add(recording.startVerse);
              }
            }
          }
          
          // Now check each chapter for completion
          for (const [chapterKey, chapterData] of Object.entries(recordingsByChapter)) {
            try {
              const actualVerseCount = getVersesForChapter(chapterData.book, chapterData.chapter).length;
              const recordedVerseCount = chapterData.recordedVerses.size;
              
              console.log(`      📊 Chapter ${chapterKey}: ${recordedVerseCount}/${actualVerseCount} verses recorded`);
              console.log(`      📊 Recorded verses: [${Array.from(chapterData.recordedVerses).sort((a,b) => a-b).join(', ')}]`);
              
              // Consider a chapter complete if ALL verses are recorded
              if (recordedVerseCount >= actualVerseCount) {
                completedChapters.add(chapterKey);
                console.log(`      ✅ Chapter ${chapterKey}: COMPLETE (${recordedVerseCount}/${actualVerseCount} verses)`);
              } else {
                console.log(`      ⏳ Chapter ${chapterKey}: INCOMPLETE (${recordedVerseCount}/${actualVerseCount} verses)`);
              }
            } catch (error) {
              console.error(`      ❌ Error processing chapter ${chapterKey}:`, error);
            }
          }
          
          const completedChaptersArray = Array.from(completedChapters).sort();
          console.log(`   📊 MIGRATION SCRIPT: Family ${family.id} has completed ${completedChaptersArray.length} chapters:`, completedChaptersArray);
          
          // Step 5: Update family completion subcollection
          console.log(`   💾 MIGRATION SCRIPT: Updating family completion subcollection...`);
          const completionRef = doc(db, `families/${family.id}/completedChapters`, 'all-chapters');
          const completionDoc = await getDoc(completionRef);
          
          if (completionDoc.exists()) {
            // Update existing document
            await updateDoc(completionRef, {
              completedChapters: completedChaptersArray,
              totalCompletedChapters: completedChaptersArray.length,
              lastUpdated: serverTimestamp(),
              migratedAt: serverTimestamp()
            });
            console.log(`   ✅ MIGRATION SCRIPT: Updated existing completion document for family ${family.id}`);
          } else {
            console.log(`   ⚠️  MIGRATION SCRIPT: No completion document found for family ${family.id}, creating new one...`);
            // Create new document with all chapters
            await createFamilyCompletionSubcollection(family.id, db);
            
            // Then update with completed chapters
            await updateDoc(completionRef, {
              completedChapters: completedChaptersArray,
              totalCompletedChapters: completedChaptersArray.length,
              lastUpdated: serverTimestamp(),
              migratedAt: serverTimestamp()
            });
            console.log(`   ✅ MIGRATION SCRIPT: Created and updated completion document for family ${family.id}`);
          }
          
          successCount++;
          processedCount++;
          
          // Small delay to prevent overwhelming Firestore
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (familyError) {
          console.error(`❌ MIGRATION SCRIPT: Error processing family ${family.id}:`, familyError);
          console.error(`❌ MIGRATION SCRIPT: Error details:`, {
            familyId: family.id,
            familyName: family.name || 'Unnamed',
            errorMessage: familyError.message,
            errorCode: familyError.code,
            errorStack: familyError.stack
          });
          errorCount++;
          processedCount++;
          // Continue with next family
        }
      }
      
      console.log(`\n🎉 MIGRATION SCRIPT: Migration process completed!`);
      console.log(`📊 MIGRATION SCRIPT: Final statistics:`);
      console.log(`   - Total families processed: ${processedCount}`);
      console.log(`   - Successful migrations: ${successCount}`);
      console.log(`   - Errors encountered: ${errorCount}`);
      console.log(`   - Success rate: ${((successCount / processedCount) * 100).toFixed(1)}%`);
      
      setMigrationProgress({ 
        current: processedCount, 
        total: families.length, 
        status: `Migration complete! Processed ${processedCount} families (${successCount} successful, ${errorCount} errors)` 
      });
      
      // Refresh the families list
      console.log('🔄 MIGRATION SCRIPT: Refreshing SuperAdmin data...');
      await fetchSuperAdminData();
      console.log('✅ MIGRATION SCRIPT: SuperAdmin data refreshed');
      
    } catch (error) {
      console.error('❌ MIGRATION SCRIPT: Critical error during migration process:', error);
      console.error('❌ MIGRATION SCRIPT: Error details:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack
      });
      setMigrationProgress({ 
        current: 0, 
        total: 0, 
        status: `Critical Error: ${error.message}` 
      });
    } finally {
      setIsMigratingData(false);
      console.log('🏁 MIGRATION SCRIPT: Migration process finished');
    }
  };

  const generateVerseUrls = (book, chapter, totalVerses) => {
    const urls = [];
    for (let verse = 1; verse <= totalVerses; verse++) {
      urls.push(`/listen?book=${book}&chapter=${chapter}&verse=${verse}`);
    }
    return urls;
  };

  // Bulk plan import — parses a dropped/selected spreadsheet client-side into
  // preview drafts. Nothing touches Firestore until the admin explicitly
  // clicks Create/Update on an individual draft below.
  const readAndParseImportFile = (file) => {
    setImportParseError('');
    if (!file) return;
    const isSpreadsheet = /\.(xlsx|xls)$/i.test(file.name);
    if (!isSpreadsheet) {
      setImportParseError('Please drop an .xlsx or .xls file.');
      return;
    }
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const drafts = parsePlansWorkbook(e.target.result);
        if (drafts.length === 0) {
          setImportParseError('No plans found — check that row 1 has a plan name in at least one column.');
        }
        setImportDrafts(drafts);
      } catch (error) {
        console.error('Error parsing plans spreadsheet:', error);
        setImportParseError('Could not read that file: ' + error.message);
      }
    };
    reader.onerror = () => setImportParseError('Could not read that file.');
    reader.readAsArrayBuffer(file);
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    setImportDragActive(false);
    readAndParseImportFile(e.dataTransfer.files?.[0]);
  };

  const handleImportFileInputChange = (e) => {
    readAndParseImportFile(e.target.files?.[0]);
    e.target.value = ''; // allow re-selecting the same file after a fix
  };

  const handleClearImport = () => {
    setImportFileName('');
    setImportDrafts([]);
    setImportParseError('');
  };

  const findExistingPlanMatch = (name) =>
    existingPlans.find((p) => (p.name || '').trim().toLowerCase() === name.trim().toLowerCase());

  const handleImportCreateOrUpdate = async (draft) => {
    const existingMatch = findExistingPlanMatch(draft.name);
    setImportBusyKey(draft.key);
    try {
      const enhancedChapters = draft.chapters.map((c) => {
        const totalVerses = getTotalVersesForChapter(c.book, c.chapter);
        return { book: c.book, chapter: c.chapter, totalVerses, verseUrls: generateVerseUrls(c.book, c.chapter, totalVerses) };
      });
      const planData = {
        name: draft.name,
        description: draft.description,
        chapters: enhancedChapters,
      };
      if (existingMatch) {
        await updateDoc(doc(db, 'plans', existingMatch.id), { ...planData, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, 'plans'), { ...planData, createdAt: serverTimestamp(), createdBy: currentUser.uid });
      }
      await fetchExistingPlans();
      setImportDrafts((prev) => prev.filter((d) => d.key !== draft.key));
    } catch (error) {
      console.error('Error importing plan:', error);
      alert(`Failed to ${existingMatch ? 'update' : 'create'} "${draft.name}": ${error.message}`);
    } finally {
      setImportBusyKey(null);
    }
  };

  const handleCreatePlan = async () => {
    if (!planName.trim() || !planDescription.trim() || !planChapters.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setIsCreatingPlan(true);
    try {
      const parsedChapters = parseChapters(planChapters);
      
      // Enhance chapters with verse counts and URLs
      const enhancedChapters = parsedChapters.map(chapter => {
        const totalVerses = getTotalVersesForChapter(chapter.book, chapter.chapter);
        return {
          ...chapter,
          totalVerses,
          verseUrls: generateVerseUrls(chapter.book, chapter.chapter, totalVerses)
        };
      });

      const planData = {
        name: planName.trim(),
        description: planDescription.trim(),
        chapters: enhancedChapters,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      };

      await addDoc(collection(db, 'plans'), planData);
      
      // Reset form
      setPlanName('');
      setPlanDescription('');
      setPlanChapters('');
      
      // Refresh plans list
      await fetchExistingPlans();
      
      alert('Plan created successfully!');
    } catch (error) {
      console.error('Error creating plan:', error);
      alert('Failed to create plan: ' + error.message);
    } finally {
      setIsCreatingPlan(false);
    }
  };

  // Backfill test functions
  const handleBackfillTest = async () => {
    setBackfillLoading(true);
    setBackfillMessage('');
    
    try {
      console.log('Starting backfill test...');
      setBackfillMessage('Starting backfill test...');
      
      await testCompletedPlansBackfill();
      
      setBackfillMessage('Backfill test completed successfully! Check your Dashboard and Profile pages to see the results.');
      console.log('Backfill test completed');
    } catch (error) {
      console.error('Backfill test failed:', error);
      setBackfillMessage(`Backfill test failed: ${error.message}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleCleanupTest = async () => {
    if (!window.confirm('Are you sure you want to clean up all test data? This will delete all completed plans.')) {
      return;
    }
    
    setBackfillLoading(true);
    setBackfillMessage('');
    
    try {
      console.log('Starting cleanup...');
      setBackfillMessage('Cleaning up test data...');
      
      await cleanupTestData();
      
      setBackfillMessage('Test data cleaned up successfully!');
      console.log('Cleanup completed');
    } catch (error) {
      console.error('Cleanup failed:', error);
      setBackfillMessage(`Cleanup failed: ${error.message}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  // Plan analytics functions
  const fetchPlanAnalytics = async () => {
    setIsLoadingAnalytics(true);
    try {
      // Get all plans
      const plansSnapshot = await getDocs(collection(db, 'plans'));
      const plans = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get all users to check their plan subscriptions
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate analytics for each plan
      const analytics = await Promise.all(plans.map(async (plan) => {
        let recordingSubscribers = 0;
        let listeningSubscribers = 0;

        // Check each user's plan subscriptions
        for (const user of users) {
          try {
            // Check recording plans subcollection
            const recordingPlansSnapshot = await getDocs(
              collection(db, `users/${user.id}/recordingPlans`)
            );
            const hasRecordingSubscription = recordingPlansSnapshot.docs.some(
              doc => doc.id === plan.id
            );
            if (hasRecordingSubscription) {
              recordingSubscribers++;
            }

            // Check listening plans subcollection
            const listeningPlansSnapshot = await getDocs(
              collection(db, `users/${user.id}/listeningPlans`)
            );
            const hasListeningSubscription = listeningPlansSnapshot.docs.some(
              doc => doc.id === plan.id
            );
            if (hasListeningSubscription) {
              listeningSubscribers++;
            }
          } catch (error) {
            // User might not have subcollections yet, skip
            console.log(`No plan subcollections for user ${user.id}`);
          }
        }

        const completionRate = recordingSubscribers > 0 
          ? Math.round((listeningSubscribers / recordingSubscribers) * 100)
          : 0;

        return {
          planId: plan.id,
          planName: plan.name,
          planDescription: plan.description,
          recordingSubscribers,
          listeningSubscribers,
          completionRate,
          totalChapters: plan.chapters?.length || 0
        };
      }));

      setPlanAnalytics(analytics);
    } catch (error) {
      console.error('Error fetching plan analytics:', error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // Helper function to safely format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        // Firestore timestamp
        return new Date(dateValue.toDate()).toLocaleDateString();
      } else if (dateValue instanceof Date) {
        // JavaScript Date object
        return dateValue.toLocaleDateString();
      } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        // String or timestamp number
        return new Date(dateValue).toLocaleDateString();
      } else {
        return 'N/A';
      }
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return 'N/A';
    }
  };

  const fetchSuperAdminData = async () => {
    setLoading(true);
    try {
      // Fetch basic stats
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const familiesSnapshot = await getDocs(collection(db, 'families'));
      const recordingsSnapshot = await getDocs(collection(db, 'recordings'));

      const totalUsers = usersSnapshot.size;
      const totalFamilies = familiesSnapshot.size;
      const totalRecordings = recordingsSnapshot.size;

      // Calculate active users (users who have recorded in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const activeUsersQuery = query(
        collection(db, 'recordings'),
        where('createdAt', '>=', thirtyDaysAgo.toISOString())
      );
      const activeRecordingsSnapshot = await getDocs(activeUsersQuery);
      const activeUserIds = new Set(activeRecordingsSnapshot.docs.map(doc => doc.data().userId));
      const activeUsers = activeUserIds.size;

      // Mock financial data (replace with actual subscription data when available)
      const premiumSubscribers = Math.floor(totalUsers * 0.15); // 15% conversion rate
      const totalRevenue = premiumSubscribers * 9.99; // $9.99/month

      setStats({
        totalUsers,
        totalFamilies,
        totalRecordings,
        activeUsers,
        premiumSubscribers,
        totalRevenue
      });

      // Fetch all users, sort by createdAt desc in JS so docs without that field still appear
      const recentUsersSnapshot = await getDocs(collection(db, 'users'));
      const recentUsersData = recentUsersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
      setRecentUsers(recentUsersData);
      setFilteredUsers(recentUsersData);

      // Fetch all families — a hardcoded limit(10) here meant older families
      // (and the search box, which only filters within whatever this query
      // returned) silently couldn't find/show anything past the 10 most
      // recently created.
      const recentFamiliesQuery = query(
        collection(db, 'families'),
        orderBy('createdAt', 'desc')
      );
      const recentFamiliesSnapshot = await getDocs(recentFamiliesQuery);
      const recentFamiliesData = recentFamiliesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Total verses recorded by the whole family — a count aggregation
      // query, not a full fetch, so this stays cheap even as recordings grow.
      const recentFamiliesWithCounts = await Promise.all(recentFamiliesData.map(async (family) => {
        try {
          const countSnap = await getCountFromServer(
            query(collection(db, 'recordings'), where('familyIds', 'array-contains', family.id))
          );
          return { ...family, recordingCount: countSnap.data().count };
        } catch (error) {
          console.error('Error counting recordings for family', family.id, error);
          return { ...family, recordingCount: null };
        }
      }));

      setRecentFamilies(recentFamiliesWithCounts);
      setFilteredFamilies(recentFamiliesWithCounts);

    } catch (error) {
      console.error('Error fetching super admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!userIsSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading Super Admin Dashboard...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-32 lg:pb-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Dashboard</h1>
        <p className="text-gray-600">Welcome back, {currentUser.email}</p>
        <div className="mt-2 text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Mobile-First Tab Navigation */}
      <div className="mb-6">
        <nav className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10 gap-2">
          {[
            { id: 'overview', name: 'Overview', Icon: ChartBarIcon },
            { id: 'users', name: 'All Users', Icon: UsersIcon },
            { id: 'families', name: 'Families', Icon: HomeIcon },
            { id: 'plans', name: 'Bible Plans', Icon: BookOpenIcon },
            { id: 'passages', name: 'Passage Plans', Icon: RectangleStackIcon },
            { id: 'analytics', name: 'Plan Analytics', Icon: PresentationChartLineIcon },
            { id: 'backfill', name: 'Backfill Test', Icon: BeakerIcon },
            { id: 'financials', name: 'Financials', Icon: CurrencyDollarIcon },
            { id: 'system', name: 'System Health', Icon: WrenchScrewdriverIcon },
            { id: 'restore', name: 'Restore', Icon: CloudArrowDownIcon },
            { id: 'testing', name: 'Testing', Icon: BellAlertIcon },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-2 rounded-lg font-medium text-xs sm:text-sm flex flex-col items-center space-y-1 transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-primary-50 text-cerulean border-2 border-primary-300 shadow-sm'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 border-2 border-transparent'
              }`}
            >
              <tab.Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-center leading-tight">{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <HomeIcon className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Families</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalFamilies.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MicrophoneIcon className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Recordings</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalRecordings.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Premium Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.premiumSubscribers.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Users</h3>
              <div className="space-y-3">
                {recentUsers.slice(0, 5).map(user => (
                  <div key={user.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-medium">
                        {user.displayName ? user.displayName[0].toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.displayName || 'Unnamed User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Families</h3>
              <div className="space-y-3">
                {recentFamilies.slice(0, 5).map(family => (
                  <div key={family.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <HomeIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {family.name || 'Unnamed Family'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {family.memberIDs ? family.memberIDs.length : 0} members
                      </p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {family.createdAt ? formatDate(family.createdAt) : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={fetchSuperAdminData}
                className="btn-primary"
              >
                Refresh Data
              </button>
              <button
                className="btn-secondary"
                onClick={backfillFamilyCompletion}
                disabled={isBackfilling}
              >
                {isBackfilling ? 'Backfilling...' : 'Backfill Family Completion'}
              </button>
              <button
                className="btn-secondary"
                onClick={migrateFamilyCompletionData}
                disabled={isMigratingData}
              >
                {isMigratingData ? 'Migrating...' : 'Migrate Completion Data'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => alert('Backup functionality coming soon!')}
              >
                Trigger Backup
              </button>
              <button
                className="btn-secondary"
                onClick={async () => {
                  try {
                    console.log('🧪 TEST: Testing family completion update...');
                    const { collection, getDocs } = await import('firebase/firestore');
                    const { db } = await import('../config/firebase');
                    const { updateFamilyCompletion } = await import('../utils/familyCompletion');
                    
                    const familiesSnapshot = await getDocs(collection(db, 'families'));
                    const families = familiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    
                    if (families.length === 0) {
                      alert('No families found');
                      return;
                    }
                    
                    const family = families[0];
                    console.log(`🧪 TEST: Testing with family ${family.id} (${family.name})`);
                    
                    await updateFamilyCompletion(family.id, 'proverbs', 1, [1, 2, 3, 4], db);
                    console.log('✅ TEST: Family completion update test completed');
                    alert('Test completed - check console for details');
                  } catch (error) {
                    console.error('❌ TEST: Error testing family completion:', error);
                    alert('Test failed - check console for details');
                  }
                }}
              >
                Test Family Completion
              </button>
              <button
                className="btn-secondary"
                onClick={() => alert('Maintenance mode coming soon!')}
              >
                Maintenance Mode
              </button>
            </div>
            
            {/* Backfill Progress */}
            {isBackfilling && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Backfill Progress</span>
                  <span className="text-sm text-blue-700">
                    {backfillProgress.current} / {backfillProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: backfillProgress.total > 0 
                        ? `${(backfillProgress.current / backfillProgress.total) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
                <p className="text-sm text-blue-700">{backfillProgress.status}</p>
              </div>
            )}
            {isMigratingData && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">Migration Progress</span>
                  <span className="text-sm text-green-700">
                    {migrationProgress.current} / {migrationProgress.total}
                  </span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: migrationProgress.total > 0 
                        ? `${(migrationProgress.current / migrationProgress.total) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
                <p className="text-sm text-green-700">{migrationProgress.status}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="card">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label htmlFor="userSearch" className="sr-only">Search users</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="userSearch"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by email, name, family relation, or bio..."
                  />
                </div>
              </div>
              {userSearchTerm && (
                <button
                  onClick={() => setUserSearchTerm('')}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} 
              {userSearchTerm && ` matching "${userSearchTerm}"`}
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Management</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Super Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Friends Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Families
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Birthday
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map(user => (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/super-admin/users/${user.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 text-sm font-medium">
                              {user.displayName ? user.displayName[0].toUpperCase() : 'U'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 hover:text-cerulean hover:underline">
                              {user.displayName || 'Unnamed User'}
                            </div>
                            {user.familyRelation && (
                              <div className="text-sm text-gray-500">
                                {user.familyRelation}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.superAdmin === true
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.superAdmin === true ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isBeta === true
                              ? 'bg-cerulean/15 text-cerulean'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {user.isBeta === true ? 'Friends' : 'No'}
                          </span>
                          <button
                            className={`text-xs px-2 py-1 rounded ${
                              user.isBeta === true
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-cerulean/10 text-cerulean hover:bg-cerulean/20'
                            }`}
                            onClick={(e) => { e.stopPropagation(); handleBetaToggle(user.id, !user.isBeta); }}
                          >
                            {user.isBeta === true ? 'Revoke' : 'Grant'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.subscriptionPlan === 'legacy' ? 'bg-cerulean/15 text-cerulean' :
                          user.subscriptionPlan === 'plus' ? 'bg-aqua-deep/15 text-aqua-deep' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {user.subscriptionPlan === 'legacy' ? 'Family Legacy' :
                           user.subscriptionPlan === 'plus' ? 'Family+' :
                           'Free'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.familyIds ? user.familyIds.length : 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="date"
                          defaultValue={user.birthday || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (user.birthday || '')) {
                              handleBirthdayChange(user.id, e.target.value);
                            }
                          }}
                          max={new Date().toISOString().slice(0, 10)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm w-[150px]"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.createdAt)}
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            className={`text-xs px-2 py-1 rounded ${
                              user.superAdmin === true
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            onClick={(e) => { e.stopPropagation(); handleSuperAdminToggle(user.id, !user.superAdmin); }}
                            disabled={user.id === currentUser.uid}
                            title={user.id === currentUser.uid ? 'Cannot modify your own status' : ''}
                          >
                            {user.superAdmin === true ? 'Remove' : 'Grant'}
                          </button>
                          <button
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                            onClick={(e) => { e.stopPropagation(); handleResetPassword(user.email); }}
                            title="Send password reset email"
                          >
                            Reset Password
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'families' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="card">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label htmlFor="familySearch" className="sr-only">Search families</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="familySearch"
                    value={familySearchTerm}
                    onChange={(e) => setFamilySearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by family name or description..."
                  />
                </div>
              </div>
              {familySearchTerm && (
                <button
                  onClick={() => setFamilySearchTerm('')}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Found {filteredFamilies.length} family{filteredFamilies.length !== 1 ? 'ies' : 'y'} 
              {familySearchTerm && ` matching "${familySearchTerm}"`}
            </p>
          </div>

          {!selectedFamily ? (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Management</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Family
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Members
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verses Recorded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFamilies.map(family => (
                      <tr key={family.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleFamilySelect(family)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <HomeIcon className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {family.name || 'Unnamed Family'}
                              </div>
                              {family.description && (
                                <div className="text-sm text-gray-500">
                                  {family.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(family.memberIDs || family.memberIds)?.length || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {family.recordingCount === null ? '—' : family.recordingCount ?? '…'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(family.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                            View Members →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedFamily.name} - Family Members
                </h3>
                <button
                  onClick={handleBackToFamilies}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  ← Back to Families
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentUsers.filter(user => user.familyIds && user.familyIds.includes(selectedFamily.id)).map(user => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 text-sm font-medium">
                                {user.displayName ? user.displayName[0].toUpperCase() : 'U'}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName || 'Unnamed User'}
                              </div>
                              {user.familyRelation && (
                                <div className="text-sm text-gray-500">
                                  {user.familyRelation}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role === 'admin' || user.role === 'creator'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role === 'creator' ? 'Creator' : user.role === 'admin' ? 'Admin' : 'Member'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="space-y-6">
          {/* Bulk Import from Spreadsheet */}
          <div className="card">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bulk Import from Spreadsheet</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  One plan per column: row 1 is the plan name, row 2 the description, and every row after
                  that is one "Book Chapter" line. Parsing only previews — nothing is saved until you click
                  Create/Update on a plan below.
                </p>
              </div>
              <button
                onClick={() => downloadTemplateWorkbook()}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                Download Template
              </button>
            </div>

            <label
              htmlFor="planImportFile"
              onDrop={handleImportDrop}
              onDragOver={(e) => { e.preventDefault(); setImportDragActive(true); }}
              onDragLeave={() => setImportDragActive(false)}
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-10 px-4 cursor-pointer transition-colors ${
                importDragActive ? 'border-cerulean bg-cerulean/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }`}
            >
              <CloudArrowUpIcon className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-cerulean">Click to browse</span> or drag an .xlsx file here
              </p>
              {importFileName && <p className="text-xs text-gray-500">{importFileName}</p>}
              <input
                id="planImportFile"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileInputChange}
                className="hidden"
              />
            </label>

            {importParseError && (
              <p className="text-sm text-red-600 mt-3">{importParseError}</p>
            )}

            {importDrafts.length > 0 && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    {importDrafts.length} plan{importDrafts.length === 1 ? '' : 's'} found
                  </p>
                  <button onClick={handleClearImport} className="text-xs text-gray-500 hover:text-gray-700 underline">
                    Clear
                  </button>
                </div>

                {importDrafts.map((draft) => {
                  const existingMatch = findExistingPlanMatch(draft.name);
                  const hasErrors = draft.errors.length > 0;
                  const isBusy = importBusyKey === draft.key;
                  return (
                    <div key={draft.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-900">{draft.name}</h4>
                            {existingMatch ? (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                Updates existing plan
                              </span>
                            ) : (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                New plan
                              </span>
                            )}
                          </div>
                          {draft.description && (
                            <p className="text-sm text-gray-600 mt-1">{draft.description}</p>
                          )}
                          <p className="text-sm text-gray-500 mt-1">{draft.chapters.length} chapters</p>
                          {draft.chapters.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {draft.chapters.map((c, i) => (
                                <span key={i} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                  {c.bookName} {c.chapter}
                                </span>
                              ))}
                            </div>
                          )}
                          {hasErrors && (
                            <div className="mt-2 bg-red-50 border border-red-200 rounded-md p-2">
                              <p className="text-xs font-semibold text-red-700 mb-1">
                                {draft.errors.length} line{draft.errors.length === 1 ? '' : 's'} couldn't be read — fix these in the spreadsheet and re-upload:
                              </p>
                              {draft.errors.map((err, i) => (
                                <p key={i} className="text-xs text-red-600">"{err.raw}" — {err.error}</p>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleImportCreateOrUpdate(draft)}
                          disabled={hasErrors || isBusy}
                          className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            existingMatch ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-cerulean hover:bg-cerulean/90 text-white'
                          }`}
                        >
                          {isBusy ? 'Saving…' : existingMatch ? 'Update Existing Plan' : 'Create New Plan'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plan Creation Form */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Bible Plan</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="planName" className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name
                </label>
                <input
                  type="text"
                  id="planName"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Essential Bible Passages"
                />
              </div>
              
              <div>
                <label htmlFor="planDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="planDescription"
                  value={planDescription}
                  onChange={(e) => setPlanDescription(e.target.value)}
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="A brief description of what this plan covers..."
                />
              </div>
              
              <SmartChapterInput
                value={planChapters}
                onChange={setPlanChapters}
                placeholder="e.g., Genesis 1, Exodus 20, Psalm 23, John 3"
                id="planChapters"
                label="Chapters (comma-separated)"
              />
              
              <div className="flex space-x-4">
                <button
                  onClick={handleCreatePlan}
                  disabled={isCreatingPlan}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingPlan ? 'Creating...' : 'Create Plan'}
                </button>
                <button
                  onClick={() => {
                    setPlanName('');
                    setPlanDescription('');
                    setPlanChapters('');
                  }}
                  className="btn-secondary"
                >
                  Clear Form
                </button>
              </div>
            </div>
          </div>

          {/* Existing Plans */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Existing Plans</h3>
            {existingPlans.length === 0 ? (
              <p className="text-gray-500">No plans created yet.</p>
            ) : (
              <div className="space-y-4">
                {existingPlans.map(plan => (
                  <div key={plan.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900">{plan.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            <strong>Chapters:</strong> {plan.chapters?.length || 0} chapters
                          </p>
                          <p className="text-sm text-gray-500">
                            <strong>Type:</strong> Chapter Plan
                          </p>
                          {plan.chapters && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500 mb-1">
                                <strong>Chapter List:</strong>
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {plan.chapters.slice(0, 10).map((chapter, index) => (
                                  <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                    {chapter.book} {chapter.chapter}
                                  </span>
                                ))}
                                {plan.chapters.length > 10 && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                                    +{plan.chapters.length - 10} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleEditPlan(plan)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Edit Plan Modal */}
          {isEditingPlan && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Plan</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="editPlanName" className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Name
                      </label>
                      <input
                        type="text"
                        id="editPlanName"
                        value={editPlanName}
                        onChange={(e) => setEditPlanName(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Essential Bible Passages"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="editPlanDescription" className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        id="editPlanDescription"
                        value={editPlanDescription}
                        onChange={(e) => setEditPlanDescription(e.target.value)}
                        rows={3}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe what this plan covers and its purpose..."
                      />
                    </div>
                    
                    <SmartChapterInput
                      value={editPlanChapters}
                      onChange={setEditPlanChapters}
                      placeholder="e.g., Genesis 1, Exodus 20, Psalm 23, John 3"
                      id="editPlanChapters"
                      label="Chapters (comma-separated)"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-4">
                        <button
                          onClick={handleSaveEdit}
                          className="btn-primary"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleDeletePlan}
                        className="text-sm text-red-600 hover:text-red-800 hover:underline"
                      >
                        Delete Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Analytics Header */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Plan Analytics</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Track plan popularity and completion rates across all families
                </p>
              </div>
              <button
                onClick={fetchPlanAnalytics}
                disabled={isLoadingAnalytics}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingAnalytics ? 'Loading...' : 'Refresh Data'}
              </button>
            </div>
          </div>

          {/* Analytics Table */}
          <div className="card">
            {isLoadingAnalytics ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">Loading analytics...</span>
              </div>
            ) : planAnalytics.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No plans found. Create some plans first!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recording Subscribers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Listening Subscribers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completion Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Chapters
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {planAnalytics.map((plan) => (
                      <tr key={plan.planId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {plan.planName}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {plan.planDescription}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {plan.recordingSubscribers}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">
                              {plan.recordingSubscribers === 1 ? 'family' : 'families'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {plan.listeningSubscribers}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">
                              {plan.listeningSubscribers === 1 ? 'family' : 'families'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            plan.completionRate >= 50 
                              ? 'bg-green-100 text-green-800'
                              : plan.completionRate >= 25
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {plan.completionRate}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {plan.totalChapters}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Analytics Summary */}
          {planAnalytics.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Total Plans</h4>
                <p className="text-2xl font-semibold text-gray-900">{planAnalytics.length}</p>
              </div>
              <div className="card">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Total Recording Subscribers</h4>
                <p className="text-2xl font-semibold text-gray-900">
                  {planAnalytics.reduce((sum, plan) => sum + plan.recordingSubscribers, 0)}
                </p>
              </div>
              <div className="card">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Total Listening Subscribers</h4>
                <p className="text-2xl font-semibold text-gray-900">
                  {planAnalytics.reduce((sum, plan) => sum + plan.listeningSubscribers, 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'financials' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Premium Subscribers</span>
                  <span className="text-lg font-semibold text-gray-900">{stats.premiumSubscribers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Conversion Rate</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {stats.totalUsers > 0 ? ((stats.premiumSubscribers / stats.totalUsers) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Monthly Revenue</span>
                  <span className="text-lg font-semibold text-green-600">
                    ${stats.totalRevenue.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Projection</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Annual Revenue</span>
                  <span className="text-lg font-semibold text-green-600">
                    ${(stats.totalRevenue * 12).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Target Subscribers</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {Math.floor(stats.totalUsers * 0.25)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Potential Revenue</span>
                  <span className="text-lg font-semibold text-blue-600">
                    ${(Math.floor(stats.totalUsers * 0.25) * 9.99 * 12).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
            <p className="text-gray-600 mb-4">
              This is a basic financial overview. Connect your payment processor to get real-time data.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Financial data shown is estimated based on user count and assumed conversion rates. 
                    Connect your actual payment processor for real-time financial data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    systemHealth.databaseStatus === 'healthy' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <span className="inline-flex items-center gap-1">
                      {systemHealth.databaseStatus === 'healthy'
                        ? <><CheckCircleIcon className="w-3.5 h-3.5" /> Healthy</>
                        : <><XCircleIcon className="w-3.5 h-3.5" /> Issues</>}
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Storage</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    systemHealth.storageStatus === 'healthy' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <span className="inline-flex items-center gap-1">
                      {systemHealth.storageStatus === 'healthy'
                        ? <><CheckCircleIcon className="w-3.5 h-3.5" /> Healthy</>
                        : <><XCircleIcon className="w-3.5 h-3.5" /> Issues</>}
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Authentication</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    systemHealth.authStatus === 'healthy' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <span className="inline-flex items-center gap-1">
                      {systemHealth.authStatus === 'healthy'
                        ? <><CheckCircleIcon className="w-3.5 h-3.5" /> Healthy</>
                        : <><XCircleIcon className="w-3.5 h-3.5" /> Issues</>}
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Last Backup</span>
                  <span className="text-sm text-gray-900">
                    {new Date(systemHealth.lastBackup).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Database Response</span>
                  <span className="text-sm font-semibold text-green-600">~50ms</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Storage Response</span>
                  <span className="text-sm font-semibold text-green-600">~200ms</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Auth Response</span>
                  <span className="text-sm font-semibold text-green-600">~100ms</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Uptime</span>
                  <span className="text-sm font-semibold text-green-600">99.9%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Apple Review Demo Family</h3>
            <p className="text-sm text-gray-500 mb-4">
              Creates (or resets) a pre-populated family with two member logins and a handful of
              placeholder recordings, so App Store/TestFlight reviewers land on a working app
              instead of an empty one. Safe to re-run any time — it resets the password and
              recordings back to a clean state.
            </p>

            <div className="mb-4 bg-gray-50 rounded-xl p-4 text-sm space-y-1">
              <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">On file with Apple</div>
              <div><span className="text-gray-500">Login email:</span> <span className="font-mono font-semibold">review.owner@familybibleapp.com</span></div>
              <div><span className="text-gray-500">Password:</span> <span className="font-mono font-semibold">AppReview2026!</span></div>
              <div className="text-gray-400 text-xs pt-1">
                These are the credentials submitted to Apple — log in as this user to see exactly what a reviewer sees.
              </div>
            </div>

            <button
              onClick={handleSeedAppleReviewFamily}
              disabled={appleSeedLoading}
              className="px-4 py-2 text-sm bg-cerulean hover:bg-cerulean/80 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {appleSeedLoading ? 'Seeding…' : appleSeedResult ? 'Re-seed Demo Family' : 'Create Demo Family'}
            </button>

            {appleSeedError && (
              <div className="mt-4 text-sm text-red-600">{appleSeedError}</div>
            )}

            {appleSeedResult && (
              <div className="mt-4 bg-gray-50 rounded-xl p-4 text-sm space-y-1">
                <div><span className="text-gray-500">Login email:</span> <span className="font-mono font-semibold">{appleSeedResult.ownerEmail}</span></div>
                <div><span className="text-gray-500">Password:</span> <span className="font-mono font-semibold">{appleSeedResult.password}</span></div>
                <div className="text-gray-400 text-xs pt-1">
                  {appleSeedResult.memberCount} members · {appleSeedResult.recordingCount} seeded recordings · family ID {appleSeedResult.familyId}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backfill Test Tab */}
      {activeTab === 'backfill' && (
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><BeakerIcon className="w-6 h-6 text-cerulean" /> Completed Plans Backfill Test</h2>
            </div>
            <p className="text-gray-600 mb-6">
              This utility will analyze your existing data and create the proper completed plans structure 
              to test the new family-level architecture. It will read all users, plans, recordings, and 
              subscriptions to backfill the completed plans data.
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">What this test will do:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Analyze all families, users, plans, and recordings</li>
                  <li>• Create completed plans for families with fully recorded plans</li>
                  <li>• Set up subscription and listening tracking arrays</li>
                  <li>• Generate realistic test data for UI testing</li>
                  <li>• Show you how the Dashboard and Profile pages will look</li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleBackfillTest}
                  disabled={backfillLoading}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {backfillLoading ? 'Running Test...' : 'Run Backfill Test'}
                </button>
                
                <button
                  onClick={handleCleanupTest}
                  disabled={backfillLoading}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {backfillLoading ? 'Cleaning...' : 'Cleanup Test Data'}
                </button>
              </div>
              
              {backfillMessage && (
                <div className={`p-4 rounded-lg ${
                  backfillMessage.includes('success')
                    ? 'bg-green-50 border border-green-200'
                    : backfillMessage.includes('failed')
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-sm ${
                    backfillMessage.includes('success')
                      ? 'text-green-800'
                      : backfillMessage.includes('failed')
                      ? 'text-red-800'
                      : 'text-blue-800'
                  }`}>
                    {backfillMessage}
                  </p>
                </div>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-900 mb-2 flex items-center gap-2"><ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" /> Important Notes:</h3>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• This will create real data in your database</li>
                  <li>• Use the cleanup button to remove test data when done</li>
                  <li>• Check the browser console for detailed logs</li>
                  <li>• After running, check your Dashboard and Profile pages</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Passages Plans Tab (global oversight — every family, public + private) ── */}
      {activeTab === 'passages' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Passage Plans</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Every Passage Plan across every family — public and private. Any user can create
                these; this view is for oversight/moderation only.
              </p>
            </div>
            <button
              onClick={loadPassagePlans}
              disabled={passagePlansLoading}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {passagePlansLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {passagePlansLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
            </div>
          )}

          {!passagePlansLoading && passagePlansLoaded && passagePlans.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <RectangleStackIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No Passage Plans have been created yet.</p>
            </div>
          )}

          {!passagePlansLoading && passagePlans.length > 0 && (
            <div className="space-y-2">
              {passagePlans.map(plan => (
                <div key={plan.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          plan.visibility === 'public' ? 'bg-cerulean/15 text-cerulean' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {plan.visibility === 'public' ? 'Public' : 'Private'}
                        </span>
                      </div>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {plan.familyName} · {plan.passages?.length || 0} passage{plan.passages?.length === 1 ? '' : 's'} · {formatDate(plan.createdAt)}
                      </p>
                      {plan.passages?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {plan.passages.map((p, i) => (
                            <span key={i} className="text-xs bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 text-gray-600">
                              {formatPassageLabel(p)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeletePassagePlan(plan)}
                      disabled={deletingPassagePlanId === plan.id}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-500 text-red-600 hover:text-white rounded-lg transition-colors disabled:opacity-40"
                    >
                      {deletingPassagePlanId === plan.id ? (
                        <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <TrashIcon className="w-3.5 h-3.5" />
                      )}
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Restore from Backup Tab ── */}
      {activeTab === 'restore' && (() => {
        const loadRestoreRecordings = async () => {
          setRestoreLoading(true);
          try {
            const snap = await getDocs(collection(db, 'recordings'));
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            all.sort((a, b) => {
              const da = a.createdAt?.toDate?.() || new Date(0);
              const db2 = b.createdAt?.toDate?.() || new Date(0);
              return db2 - da;
            });
            setRestoreRecordings(all);
            // Seed already-restored IDs
            setRestoredIds(new Set(all.filter(r => r.restored).map(r => r.id)));
          } catch (err) {
            console.error('Error loading recordings for restore:', err);
          } finally {
            setRestoreLoading(false);
          }
        };

        const restoreOne = async (rec) => {
          if (restoringIds.has(rec.id)) return;
          setRestoringIds(prev => new Set([...prev, rec.id]));
          try {
            const reqRef = await addDoc(collection(db, 'restoreRequests'), {
              recordingId: rec.id,
              requestedBy: currentUser.uid,
              status: 'pending',
              createdAt: serverTimestamp(),
            });
            await new Promise((resolve, reject) => {
              const unsub = onSnapshot(reqRef, (snap) => {
                const status = snap.data()?.status;
                if (status === 'done') { unsub(); resolve(); }
                else if (status === 'error') { unsub(); reject(new Error(snap.data()?.error || 'Restore failed')); }
              });
              setTimeout(() => { unsub(); reject(new Error('Timed out after 3 minutes')); }, 180000);
            });
            setRestoredIds(prev => new Set([...prev, rec.id]));
          } catch (err) {
            alert(`Failed to restore ${rec.passage}: ${err.message}`);
          } finally {
            setRestoringIds(prev => { const s = new Set(prev); s.delete(rec.id); return s; });
          }
        };

        const playRecording = (rec) => {
          if (playingId === rec.id) {
            audioRef.current?.pause();
            setPlayingId(null);
            return;
          }
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = rec.audioURL || rec.audioUrl || '';
            audioRef.current.play().catch(() => {});
          }
          setPlayingId(rec.id);
        };

        // ── Category selection screen ──
        if (!restoreCategory) {
          return (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Restore from Backup</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Pull original files from Backblaze B2 back into Firebase Storage.
                  Select a category to get started.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                <button
                  onClick={() => {
                    setRestoreCategory('recordings');
                    setRestoreRecordings([]);
                    setRestoredIds(new Set());
                  }}
                  className="flex items-center gap-4 p-5 bg-white border-2 border-gray-200 rounded-2xl hover:border-cerulean hover:bg-cerulean/5 transition-all text-left group"
                >
                  <div className="w-12 h-12 rounded-xl bg-cerulean/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cerulean/20 transition-colors">
                    <MicrophoneIcon className="w-6 h-6 text-cerulean" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Recordings</p>
                    <p className="text-xs text-gray-500 mt-0.5">Verse audio recordings</p>
                  </div>
                </button>
              </div>
            </div>
          );
        }

        const restoreAll = async () => {
          const unrestored = restoreRecordings.filter(r => !restoredIds.has(r.id));
          if (!unrestored.length) return;
          if (!window.confirm(`Restore all ${unrestored.length} recordings from Backblaze B2?\n\nThis will pull the oldest (pre-trim) version of each file. This may take several minutes.`)) return;
          setBatchRestoring(true);
          setBatchRestoreProgress({ done: 0, total: unrestored.length });
          for (let i = 0; i < unrestored.length; i++) {
            await restoreOne(unrestored[i]);
            setBatchRestoreProgress({ done: i + 1, total: unrestored.length });
          }
          setBatchRestoring(false);
        };

        // ── Recordings list screen ──
        return (
          <div className="space-y-4">
            {/* Hidden audio element */}
            <audio ref={audioRef} onEnded={() => setPlayingId(null)} />

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setRestoreCategory(null); setRestoreRecordings([]); }}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" /> Categories
                </button>
                <span className="text-gray-300">/</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Restore Recordings</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Pulls oldest B2 version (pre-trim original) · marks trimmed=false so you can re-trim later
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={loadRestoreRecordings}
                  disabled={restoreLoading || batchRestoring}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {restoreLoading ? 'Loading…' : restoreRecordings.length ? 'Refresh' : 'Load Recordings'}
                </button>
                {restoreRecordings.length > 0 && (
                  <button
                    onClick={restoreAll}
                    disabled={batchRestoring || restoreRecordings.every(r => restoredIds.has(r.id)) || restoreLoading}
                    className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
                  >
                    <CloudArrowDownIcon className="w-4 h-4" />
                    {batchRestoring
                      ? `Restoring ${batchRestoreProgress.done}/${batchRestoreProgress.total}…`
                      : `Restore All (${restoreRecordings.filter(r => !restoredIds.has(r.id)).length})`}
                  </button>
                )}
              </div>
            </div>

            {/* Batch progress bar */}
            {batchRestoring && (
              <div className="bg-orange-50 rounded-xl p-3">
                <div className="flex items-center justify-between text-sm text-orange-600 font-medium mb-2">
                  <span>Restoring from Backblaze B2…</span>
                  <span>{batchRestoreProgress.done} / {batchRestoreProgress.total}</span>
                </div>
                <div className="h-2 rounded-full bg-orange-100 overflow-hidden">
                  <div
                    className="h-full bg-orange-500 transition-all duration-300"
                    style={{ width: `${batchRestoreProgress.total ? (batchRestoreProgress.done / batchRestoreProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Stats */}
            {restoreRecordings.length > 0 && (
              <div className="flex gap-4 text-sm">
                <span className="text-gray-500">{restoreRecordings.length} total recordings</span>
                <span className="text-green-600 font-medium">{restoredIds.size} restored this session</span>
                <span className="text-orange-500 font-medium">{restoreRecordings.length - restoredIds.size} remaining</span>
              </div>
            )}

            {/* Load prompt */}
            {restoreRecordings.length === 0 && !restoreLoading && (
              <div className="text-center py-16 text-gray-400">
                <CloudArrowDownIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Click Load Recordings to see live recordings</p>
              </div>
            )}

            {restoreLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
              </div>
            )}

            {/* Recordings list */}
            {restoreRecordings.length > 0 && (
              <div className="space-y-2">
                {restoreRecordings.map(rec => {
                  const isRestored = restoredIds.has(rec.id);
                  const isRestoringNow = restoringIds.has(rec.id);
                  const isPlaying = playingId === rec.id;

                  return (
                    <div
                      key={rec.id}
                      className={`rounded-xl border p-4 flex items-center gap-3 transition-colors ${
                        isRestored
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      {/* Play/pause */}
                      <button
                        onClick={() => playRecording(rec)}
                        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                          isPlaying
                            ? 'bg-cerulean text-white'
                            : 'bg-gray-100 hover:bg-cerulean/10 text-gray-600 hover:text-cerulean'
                        }`}
                        title={isPlaying ? 'Pause' : 'Play current version'}
                      >
                        {isPlaying ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{rec.passage}</p>
                        <p className="text-xs text-gray-400 truncate">
                          {rec.name || rec.userEmail} · {rec.createdAt?.toDate?.()?.toLocaleDateString() || '—'}
                          {rec.trimmed === false && rec.restored && (
                            <span className="ml-2 text-amber-500 font-medium">previously restored · needs trim</span>
                          )}
                        </p>
                      </div>

                      {/* Status + action */}
                      {isRestored ? (
                        <span className="flex-shrink-0 flex items-center gap-1 text-xs text-green-600 font-semibold">
                          <CheckCircleIcon className="w-4 h-4" /> Restored
                        </span>
                      ) : isRestoringNow ? (
                        <span className="flex-shrink-0 flex items-center gap-1.5 text-xs text-cerulean font-semibold">
                          <div className="w-3.5 h-3.5 border-2 border-cerulean border-t-transparent rounded-full animate-spin" />
                          Restoring…
                        </span>
                      ) : (
                        <button
                          onClick={() => restoreOne(rec)}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-orange-50 hover:bg-orange-500 text-orange-600 hover:text-white rounded-lg transition-colors border border-orange-200 hover:border-orange-500"
                        >
                          <CloudArrowDownIcon className="w-3.5 h-3.5" /> Restore
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
      {activeTab === 'testing' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <BellAlertIcon className="w-5 h-5 text-cerulean" />
              Notification Testing
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Settings that only affect your super admin account during testing. These do not change behavior for regular users.
            </p>

            <div className="divide-y divide-gray-100">
              {/* Self-notification toggle */}
              <div className="flex items-start justify-between py-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Receive your own recording notifications</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    By default, the person who made a recording is excluded from receiving a notification about it.
                    Turn this on to notify yourself when you upload — useful for testing the full notification pipeline.
                  </p>
                </div>
                <button
                  onClick={() => handleNotificationTestModeToggle(!notificationTestMode)}
                  disabled={testModeLoading}
                  className={`relative flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                    notificationTestMode ? 'bg-cerulean' : 'bg-gray-200'
                  } ${testModeLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  role="switch"
                  aria-checked={notificationTestMode}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      notificationTestMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className={`mt-4 rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${notificationTestMode ? 'bg-cerulean/10 text-cerulean' : 'bg-gray-50 text-gray-500'}`}>
              {notificationTestMode
                ? <><CheckCircleIcon className="w-4 h-4 flex-shrink-0" /> Test mode on — you will receive notifications for your own recordings.</>
                : <><XCircleIcon className="w-4 h-4 flex-shrink-0" /> Test mode off — your own recordings will not notify you (normal behavior).</>
              }
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdmin;
